import { ComponentType, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Link,
  Sidebar,
  SidebarDivider,
  useSidebarOpenState,
} from '@backstage/core-components';
import {
  NavContentBlueprint,
  NavContentComponent,
} from '@backstage/plugin-app-react';
import { makeStyles, SvgIconProps } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/HomeOutlined';
import CategoryIcon from '@material-ui/icons/CategoryOutlined';
import ExtensionIcon from '@material-ui/icons/ExtensionOutlined';
import DescriptionIcon from '@material-ui/icons/DescriptionOutlined';
import AddCircleIcon from '@material-ui/icons/AddCircleOutline';
import SettingsIcon from '@material-ui/icons/SettingsOutlined';
import VisibilityIcon from '@material-ui/icons/VisibilityOutlined';
import { APIS_CATALOG_PATH, isCatalogApis } from '../apis/paths';
import { useByxT } from '../shared/translations';
import { spaceGrotesk } from '../theme/fonts';
import { byxBrand, byxSidebar } from '../theme/tokens';
import { SidebarLogo } from './SidebarLogo';

const useStyles = makeStyles({
  menu: {
    flex: '1 0 auto',
    alignSelf: 'stretch',
    display: 'flex',
    flexDirection: 'column',
  },
  spacer: { flex: '1 0 auto' },
  groupLabel: {
    display: 'block',
    padding: '16px 24px 8px',
    fontFamily: spaceGrotesk,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.08em',
    lineHeight: '16px',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
    color: byxSidebar.groupLabel,
  },
  list: {
    margin: 0,
    padding: 0,
    listStyle: 'none',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    height: 44,
    padding: '0 24px',
    color: byxSidebar.inkMuted,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: byxSidebar.hover,
      textDecoration: 'none',
    },
    '&:focus-visible': {
      outline: `2px solid ${byxSidebar.ink}`,
      outlineOffset: -2,
    },
  },
  closed: {
    justifyContent: 'center',
    padding: 0,
  },
  active: {
    color: byxSidebar.ink,
    fontWeight: 600,
    backgroundColor: byxSidebar.hover,
    // The amber marker as an inset shadow, so the row does not shift.
    boxShadow: `inset 3px 0 0 ${byxBrand.amber}`,
  },
  icon: {
    flex: 'none',
    fontSize: 20,
  },
});

/** True when `pathname` is the page at `href` or one of its sub-pages. */
export function isActivePath(pathname: string, href: string): boolean {
  const base = href.split('?')[0].replace(/\/$/, '');
  if (base === '') {
    return pathname === '/' || pathname === '';
  }
  return pathname === base || pathname.startsWith(`${base}/`);
}

type MenuItemProps = {
  to: string;
  label: string;
  icon: ComponentType<SvgIconProps>;
  active: boolean;
};

function MenuItem(props: Readonly<MenuItemProps>) {
  const { to, label, icon: Icon, active } = props;
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  const className = [
    classes.item,
    isOpen ? '' : classes.closed,
    active ? classes.active : '',
  ].join(' ');
  return (
    <li>
      <Link
        to={to}
        underline="none"
        className={className}
        aria-current={active ? 'page' : undefined}
        aria-label={isOpen ? undefined : label}
      >
        <Icon aria-hidden className={classes.icon} />
        {isOpen && label}
      </Link>
    </li>
  );
}

function MenuGroup(props: Readonly<{ label: string; children: ReactNode }>) {
  const classes = useStyles();
  const { isOpen } = useSidebarOpenState();
  // The list carries the group name; the visible label is decoration.
  return (
    <div>
      {isOpen && (
        <span className={classes.groupLabel} aria-hidden="true">
          {props.label}
        </span>
      )}
      <ul className={classes.list} aria-label={props.label}>
        {props.children}
      </ul>
    </div>
  );
}

/**
 * Side menu of the v2 design (AD-12): "Espaço de trabalho" on top and
 * "Admin" at the bottom, with a fixed set of items. Pages that are not
 * listed (Catalog Graph, Registrar existente, search, notifications…) stay
 * routable; a plugin installed later only appears here if added explicitly.
 *
 * "APIs" is the Catálogo on its APIs tab; it is the active item there, and
 * "Catálogo" is active on every other Catálogo page. "Admin" is only a
 * label: it does not restrict access (DR-5).
 */
export const ByxSidebar: NavContentComponent = ({ navItems }) => {
  const classes = useStyles();
  const { t } = useByxT();
  const location = useLocation();
  const onApis = isCatalogApis(location);

  const item = (
    id: string,
    label: string,
    icon: MenuItemProps['icon'],
    active?: (href: string) => boolean,
  ) => {
    const navItem = navItems.take(id);
    if (!navItem) {
      return null;
    }
    const href = navItem.href;
    return (
      <MenuItem
        key={id}
        to={href}
        label={label}
        icon={icon}
        active={(active ?? (h => isActivePath(location.pathname, h)))(href)}
      />
    );
  };

  const home = item('page:home', t('nav.home'), HomeIcon);
  const catalog = item(
    'page:catalog',
    t('nav.catalog'),
    CategoryIcon,
    href => !onApis && isActivePath(location.pathname, href),
  );
  const docs = item('page:techdocs', t('nav.docs'), DescriptionIcon);
  const create = item('page:scaffolder', t('nav.create'), AddCircleIcon);
  const settings = item('page:user-settings', t('nav.settings'), SettingsIcon);
  const visualizer = item(
    'page:app-visualizer',
    t('nav.visualizer'),
    VisibilityIcon,
  );
  // Routable pages that are not menu items: taken so they are not rendered.
  for (const id of [
    'page:catalog-graph',
    'page:catalog-import',
    'page:search',
    'page:notifications',
  ]) {
    navItems.take(id);
  }

  return (
    <Sidebar>
      <SidebarLogo />
      <nav aria-label={t('nav.label')} className={classes.menu}>
        <MenuGroup label={t('nav.workspace')}>
          {home}
          {catalog}
          <MenuItem
            to={APIS_CATALOG_PATH}
            label={t('nav.apis')}
            icon={ExtensionIcon}
            active={onApis}
          />
          {docs}
          {create}
        </MenuGroup>
        <div className={classes.spacer} />
        <SidebarDivider />
        <MenuGroup label={t('nav.admin')}>
          {settings}
          {visualizer}
        </MenuGroup>
      </nav>
    </Sidebar>
  );
};

export const SidebarContent = NavContentBlueprint.make({
  params: { component: ByxSidebar },
});
